import React, { useEffect, useMemo, useState } from "react";
import {
  Clock3,
  LifeBuoy,
  MessageSquare,
  Plus,
  RefreshCw,
  Search,
  Send,
  ShieldAlert,
  UserCircle2
} from "lucide-react";
import axios from "axios";
import { getApiUrl } from "../../config/api";

interface Ticket {
  id: number;
  subject: string;
  status: "open" | "in_progress" | "resolved";
  requester_name?: string;
  requester_email?: string;
  requester_role?: string;
  created_at?: string;
  updated_at?: string;
}

interface TicketMessage {
  id: number;
  sender_name: string;
  sender_email?: string;
  sender_role: string;
  message: string;
  created_at?: string;
}

interface TicketListResponse {
  manageableTickets?: Ticket[];
  myTickets?: Ticket[];
  summary?: {
    manageable?: number;
    mine?: number;
    totalVisible?: number;
    open?: number;
    resolved?: number;
  };
}

const SupportDesk: React.FC = () => {
  const token = localStorage.getItem("token") || "";
  const role = (localStorage.getItem("role") || "").toLowerCase();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [manageableTickets, setManageableTickets] = useState<Ticket[]>([]);
  const [myTickets, setMyTickets] = useState<Ticket[]>([]);
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [selectedScope, setSelectedScope] = useState<"manageable" | "mine">("mine");
  const [messages, setMessages] = useState<TicketMessage[]>([]);
  const [search, setSearch] = useState("");
  const [subject, setSubject] = useState("");
  const [draft, setDraft] = useState("");
  const [reply, setReply] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const canManageStatuses = role === "admin" && selectedScope === "manageable";
  const canBrowseTenantTickets = role === "admin";

  const normalizeListResponse = (payload: TicketListResponse | Ticket[]) => {
    if (Array.isArray(payload)) {
      return {
        manageableTickets: canBrowseTenantTickets ? payload : [],
        myTickets: canBrowseTenantTickets ? [] : payload
      };
    }

    return {
      manageableTickets: Array.isArray(payload?.manageableTickets) ? payload.manageableTickets : [],
      myTickets: Array.isArray(payload?.myTickets) ? payload.myTickets : []
    };
  };

  const loadTickets = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await axios.get(getApiUrl("support/tickets"), { headers });
      const lists = normalizeListResponse(response.data);
      setManageableTickets(lists.manageableTickets);
      setMyTickets(lists.myTickets);

      const nextManageable = selectedTicket ? lists.manageableTickets.find((ticket) => ticket.id === selectedTicket.id) : null;
      const nextMine = selectedTicket ? lists.myTickets.find((ticket) => ticket.id === selectedTicket.id) : null;

      if (nextManageable) {
        setSelectedTicket(nextManageable);
        setSelectedScope("manageable");
      } else if (nextMine) {
        setSelectedTicket(nextMine);
        setSelectedScope("mine");
      } else if (lists.manageableTickets.length > 0) {
        setSelectedTicket(lists.manageableTickets[0]);
        setSelectedScope("manageable");
      } else if (lists.myTickets.length > 0) {
        setSelectedTicket(lists.myTickets[0]);
        setSelectedScope("mine");
      } else {
        setSelectedTicket(null);
        setSelectedScope("mine");
        setMessages([]);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to load support tickets");
    } finally {
      setLoading(false);
    }
  };

  const loadTicket = async (ticketId: number) => {
    try {
      setError("");
      const response = await axios.get(getApiUrl(`support/tickets/${ticketId}`), { headers });
      setSelectedTicket(response.data.ticket || null);
      setMessages(Array.isArray(response.data.messages) ? response.data.messages : []);
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to load support ticket");
    }
  };

  useEffect(() => {
    loadTickets();
  }, []);

  useEffect(() => {
    if (selectedTicket?.id) {
      loadTicket(selectedTicket.id);
    }
  }, [selectedTicket?.id]);

  const filterTickets = (tickets: Ticket[]) => tickets.filter((ticket) => {
    const text = `${ticket.subject} ${ticket.requester_name || ""} ${ticket.requester_email || ""}`.toLowerCase();
    return text.includes(search.toLowerCase());
  });
  const filteredManageableTickets = filterTickets(manageableTickets);
  const filteredMyTickets = filterTickets(myTickets);
  const primaryTickets = canBrowseTenantTickets ? filteredManageableTickets : filteredMyTickets;
  const ticketSummary = {
    total: canBrowseTenantTickets ? manageableTickets.length : myTickets.length,
    open: (canBrowseTenantTickets ? manageableTickets : myTickets).filter((ticket) => ticket.status === "open").length,
    resolved: (canBrowseTenantTickets ? manageableTickets : myTickets).filter((ticket) => ticket.status === "resolved").length,
    mine: myTickets.length
  };

  const heroTitle = canBrowseTenantTickets ? "School Support Desk" : "My Support Requests";
  const heroCopy = canBrowseTenantTickets
    ? "Handle support requests raised by teachers and school leaders, and keep your own escalations to platform support separate."
    : "Report an issue, track your own request history, and follow replies from your school administrator.";
  const ticketListTitle = canBrowseTenantTickets ? "School Support Requests" : "Your Requests";
  const ticketListHint = canBrowseTenantTickets
    ? "Requests from teachers and staff that require school-level follow-up."
    : "Only your own support requests are shown here.";
  const threadEmptyCopy = canBrowseTenantTickets
    ? "Choose a school support request or one of your escalations to review the full conversation."
    : "Choose one of your requests to read the full support conversation.";

  const createTicket = async () => {
    if (!subject.trim() || !draft.trim()) {
      setError("Subject and message are required");
      return;
    }
    try {
      setSubmitting(true);
      setError("");
      setNotice("");
      const response = await axios.post(
        getApiUrl("support/tickets"),
        { subject: subject.trim(), message: draft.trim() },
        { headers }
      );
      setSubject("");
      setDraft("");
      setNotice(canBrowseTenantTickets ? "Escalation sent to platform support." : "Support ticket submitted.");
      await loadTickets();
      if (response.data?.ticketId) {
        await loadTicket(response.data.ticketId);
        setSelectedScope("mine");
      }
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to submit support ticket");
    } finally {
      setSubmitting(false);
    }
  };

  const sendReply = async () => {
    if (!selectedTicket || !reply.trim()) return;
    try {
      setSubmitting(true);
      setError("");
      await axios.post(
        getApiUrl(`support/tickets/${selectedTicket.id}/reply`),
        { message: reply.trim() },
        { headers }
      );
      setReply("");
      await loadTicket(selectedTicket.id);
      await loadTickets();
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to send reply");
    } finally {
      setSubmitting(false);
    }
  };

  const updateStatus = async (status: Ticket["status"]) => {
    if (!selectedTicket) return;
    try {
      setError("");
      await axios.patch(
        getApiUrl(`support/tickets/${selectedTicket.id}`),
        { status },
        { headers }
      );
      await loadTicket(selectedTicket.id);
      await loadTickets();
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to update ticket status");
    }
  };

  const selectTicket = (ticket: Ticket, scope: "manageable" | "mine") => {
    setSelectedScope(scope);
    setSelectedTicket(ticket);
    loadTicket(ticket.id);
  };

  const selectedRequesterLabel = selectedScope === "manageable"
    ? selectedTicket?.requester_name || selectedTicket?.requester_email || "School staff"
    : canBrowseTenantTickets
      ? "Escalated by you"
      : "Submitted by you";

  const selectedThreadHint = selectedScope === "manageable"
    ? "This request was raised by a school user and can be updated by the school admin."
    : canBrowseTenantTickets
      ? "This request was escalated to platform support. You can reply, but not manage its status."
      : "Replies from your school admin will appear in this thread.";

  return (
    <div className="min-h-screen bg-slate-50/50 p-6 lg:p-10">
      <div className="max-w-[1450px] mx-auto">
        <div className="mb-8 flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <p className="text-[11px] font-black uppercase tracking-[0.28em] text-slate-400">Help Center</p>
            <h1 className="text-3xl font-bold tracking-tight text-slate-950 mt-3">{heroTitle}</h1>
            <p className="text-slate-500 mt-2 max-w-3xl">{heroCopy}</p>
          </div>
          <button
            type="button"
            onClick={loadTickets}
            className="px-4 py-3 rounded-2xl border border-slate-200 bg-white text-sm font-semibold text-slate-700 inline-flex items-center gap-2 w-fit"
          >
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>

        {error ? <div className="mb-5 rounded-2xl border border-rose-200 bg-rose-50 px-5 py-4 text-sm font-semibold text-rose-700">{error}</div> : null}
        {notice ? <div className="mb-5 rounded-2xl border border-emerald-200 bg-emerald-50 px-5 py-4 text-sm font-semibold text-emerald-700">{notice}</div> : null}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-6">
          <div className="bg-white rounded-[1.75rem] border border-slate-200 shadow-sm p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-slate-400">{canBrowseTenantTickets ? "School requests" : "My tickets"}</p>
                <p className="text-3xl font-bold text-slate-950 mt-3">{ticketSummary.total}</p>
              </div>
              <div className="rounded-2xl bg-slate-100 p-3 text-slate-700"><LifeBuoy size={18} /></div>
            </div>
          </div>
          <div className="bg-white rounded-[1.75rem] border border-slate-200 shadow-sm p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-slate-400">In progress</p>
                <p className="text-3xl font-bold text-slate-950 mt-3">
                  {(canBrowseTenantTickets ? manageableTickets : myTickets).filter((ticket) => ticket.status === "in_progress").length}
                </p>
              </div>
              <div className="rounded-2xl bg-amber-50 p-3 text-amber-700"><Clock3 size={18} /></div>
            </div>
          </div>
          <div className="bg-white rounded-[1.75rem] border border-slate-200 shadow-sm p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-slate-400">Resolved</p>
                <p className="text-3xl font-bold text-slate-950 mt-3">{ticketSummary.resolved}</p>
              </div>
              <div className="rounded-2xl bg-emerald-50 p-3 text-emerald-700"><MessageSquare size={18} /></div>
            </div>
          </div>
          {canBrowseTenantTickets ? (
            <div className="bg-white rounded-[1.75rem] border border-slate-200 shadow-sm p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-slate-400">My escalations</p>
                  <p className="text-3xl font-bold text-slate-950 mt-3">{ticketSummary.mine}</p>
                </div>
                <div className="rounded-2xl bg-sky-50 p-3 text-sky-700"><ShieldAlert size={18} /></div>
              </div>
            </div>
          ) : null}
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-[360px_minmax(0,1fr)] gap-6">
          <aside className="space-y-6">
            <section className="bg-white rounded-[1.75rem] border border-slate-200 shadow-sm p-5">
              <div className="flex items-center gap-2 mb-4">
                <Plus size={18} className="text-teal-600" />
                <h2 className="text-lg font-bold text-slate-900">Report an Issue</h2>
              </div>
              <div className="space-y-4">
                <label className="block">
                  <span className="text-[11px] uppercase tracking-[0.22em] font-bold text-slate-400">Subject</span>
                  <input
                    className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 outline-none focus:border-teal-500 focus:ring-4 focus:ring-teal-500/5"
                    value={subject}
                    onChange={(event) => setSubject(event.target.value)}
                    placeholder="Billing issue, access problem, timetable error..."
                  />
                </label>
                <label className="block">
                  <span className="text-[11px] uppercase tracking-[0.22em] font-bold text-slate-400">Message</span>
                  <textarea
                    className="mt-2 w-full min-h-[180px] rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 outline-none resize-y focus:border-teal-500 focus:ring-4 focus:ring-teal-500/5"
                    value={draft}
                    onChange={(event) => setDraft(event.target.value)}
                    placeholder="Describe the issue, expected behavior, and any urgency."
                  />
                </label>
                <button
                  type="button"
                  onClick={createTicket}
                  disabled={submitting}
                  className="w-full rounded-2xl bg-slate-900 text-white px-4 py-3 text-sm font-semibold disabled:opacity-60 inline-flex items-center justify-center gap-2"
                >
                  <Send size={15} />
                  {canBrowseTenantTickets ? "Escalate to Platform Support" : "Submit Request"}
                </button>
              </div>
            </section>

            <section className="bg-white rounded-[1.75rem] border border-slate-200 shadow-sm p-5">
              <div className="flex items-center gap-2 mb-4">
                <LifeBuoy size={18} className="text-teal-600" />
                <div>
                  <h2 className="text-lg font-bold text-slate-900">{ticketListTitle}</h2>
                  <p className="text-xs text-slate-400 mt-1">{ticketListHint}</p>
                </div>
              </div>
              <div className="relative mb-4">
                <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  className="w-full rounded-2xl border border-slate-200 bg-white pl-11 pr-4 py-3 text-sm text-slate-700 outline-none focus:border-teal-500 focus:ring-4 focus:ring-teal-500/5"
                  placeholder="Search tickets..."
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                />
              </div>
              <div className="space-y-3 max-h-[480px] overflow-y-auto pr-1">
                {!loading && primaryTickets.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-center">
                    <ShieldAlert size={20} className="mx-auto text-slate-300 mb-3" />
                    <p className="text-sm font-semibold text-slate-600">{canBrowseTenantTickets ? "No school requests yet" : "No support requests yet"}</p>
                    <p className="text-xs text-slate-400 mt-1">
                      {canBrowseTenantTickets
                        ? "Requests from teachers and staff will appear here."
                        : "Create a ticket to contact your school admin. Replies will appear here."}
                    </p>
                  </div>
                ) : primaryTickets.map((ticket) => (
                  <button
                    key={ticket.id}
                    type="button"
                    onClick={() => selectTicket(ticket, canBrowseTenantTickets ? "manageable" : "mine")}
                    className={`w-full text-left rounded-2xl border p-4 transition ${selectedTicket?.id === ticket.id && selectedScope === (canBrowseTenantTickets ? "manageable" : "mine") ? "border-teal-300 bg-teal-50" : "border-slate-200 bg-white hover:bg-slate-50"}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold text-slate-900">{ticket.subject}</p>
                        <p className="text-xs text-slate-500 mt-1">
                          {canBrowseTenantTickets
                            ? (ticket.requester_name || ticket.requester_email || "School requester")
                            : "Your request"}
                        </p>
                      </div>
                      <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.2em] ${
                        ticket.status === "resolved"
                          ? "bg-emerald-100 text-emerald-700"
                          : ticket.status === "in_progress"
                            ? "bg-amber-100 text-amber-700"
                            : "bg-slate-100 text-slate-700"
                      }`}>
                        {ticket.status.replace("_", " ")}
                      </span>
                    </div>
                    <p className="text-xs text-slate-400 mt-3">{ticket.updated_at ? new Date(ticket.updated_at).toLocaleString() : ""}</p>
                  </button>
                ))}
              </div>
            </section>

            {canBrowseTenantTickets ? (
              <section className="bg-white rounded-[1.75rem] border border-slate-200 shadow-sm p-5">
                <div className="flex items-center gap-2 mb-4">
                  <MessageSquare size={18} className="text-sky-600" />
                  <div>
                    <h2 className="text-lg font-bold text-slate-900">My Escalations</h2>
                    <p className="text-xs text-slate-400 mt-1">These requests were sent to platform support.</p>
                  </div>
                </div>
                <div className="space-y-3 max-h-[320px] overflow-y-auto pr-1">
                  {filteredMyTickets.length === 0 ? (
                    <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-center">
                      <ShieldAlert size={20} className="mx-auto text-slate-300 mb-3" />
                      <p className="text-sm font-semibold text-slate-600">No escalations yet</p>
                      <p className="text-xs text-slate-400 mt-1">When you contact platform support directly, the thread will appear here.</p>
                    </div>
                  ) : filteredMyTickets.map((ticket) => (
                    <button
                      key={ticket.id}
                      type="button"
                      onClick={() => selectTicket(ticket, "mine")}
                      className={`w-full text-left rounded-2xl border p-4 transition ${selectedTicket?.id === ticket.id && selectedScope === "mine" ? "border-sky-300 bg-sky-50" : "border-slate-200 bg-white hover:bg-slate-50"}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-sm font-semibold text-slate-900">{ticket.subject}</p>
                          <p className="text-xs text-slate-500 mt-1">Escalated by you</p>
                        </div>
                        <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.2em] ${
                          ticket.status === "resolved"
                            ? "bg-emerald-100 text-emerald-700"
                            : ticket.status === "in_progress"
                              ? "bg-amber-100 text-amber-700"
                              : "bg-slate-100 text-slate-700"
                        }`}>
                          {ticket.status.replace("_", " ")}
                        </span>
                      </div>
                      <p className="text-xs text-slate-400 mt-3">{ticket.updated_at ? new Date(ticket.updated_at).toLocaleString() : ""}</p>
                    </button>
                  ))}
                </div>
              </section>
            ) : null}
          </aside>

          <section className="bg-white rounded-[1.75rem] border border-slate-200 shadow-sm p-6">
            {!selectedTicket ? (
              <div className="min-h-[620px] grid place-items-center text-center px-6">
                <div>
                  <MessageSquare size={28} className="mx-auto text-slate-300 mb-4" />
                  <p className="text-lg font-semibold text-slate-700">No request selected</p>
                  <p className="text-sm text-slate-400 mt-2">{threadEmptyCopy}</p>
                </div>
              </div>
            ) : (
              <div className="min-h-[620px] flex flex-col">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 pb-5 border-b border-slate-100">
                  <div>
                    <h2 className="text-2xl font-bold text-slate-950">{selectedTicket.subject}</h2>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm text-slate-500">
                      <span>Status: {selectedTicket.status.replace("_", " ")}</span>
                      <span>Opened: {selectedTicket.created_at ? new Date(selectedTicket.created_at).toLocaleString() : "Unknown"}</span>
                      {selectedScope === "manageable" && selectedTicket.requester_name ? <span>Requester: {selectedTicket.requester_name}</span> : null}
                      <span>{selectedRequesterLabel}</span>
                    </div>
                    <p className="text-sm text-slate-500 mt-3">{selectedThreadHint}</p>
                  </div>
                  {canManageStatuses ? (
                    <div className="flex flex-wrap gap-2">
                      <button type="button" onClick={() => updateStatus("open")} className="px-3 py-2 rounded-xl bg-slate-100 text-slate-700 text-xs font-semibold uppercase tracking-[0.18em]">Mark Open</button>
                      <button type="button" onClick={() => updateStatus("in_progress")} className="px-3 py-2 rounded-xl bg-amber-100 text-amber-700 text-xs font-semibold uppercase tracking-[0.18em]">Mark In Progress</button>
                      <button type="button" onClick={() => updateStatus("resolved")} className="px-3 py-2 rounded-xl bg-emerald-100 text-emerald-700 text-xs font-semibold uppercase tracking-[0.18em]">Mark Resolved</button>
                    </div>
                  ) : null}
                </div>

                <div className="flex-1 overflow-y-auto py-5 space-y-4">
                  {messages.length === 0 ? (
                    <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">
                      No thread messages yet.
                    </div>
                  ) : messages.map((message) => (
                    <article key={message.id} className="rounded-2xl border border-slate-200 bg-slate-50/70 px-5 py-4">
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
                        <span className="font-semibold text-slate-800 inline-flex items-center gap-1"><UserCircle2 size={14} />{message.sender_name}</span>
                      <span className="uppercase tracking-[0.18em]">{message.sender_role}</span>
                        <span>{message.created_at ? new Date(message.created_at).toLocaleString() : ""}</span>
                      </div>
                      <p className="text-sm text-slate-700 mt-3 whitespace-pre-wrap">{message.message}</p>
                    </article>
                  ))}
                </div>

                <div className="pt-5 border-t border-slate-100">
                  <label className="block">
                    <span className="text-[11px] uppercase tracking-[0.22em] font-bold text-slate-400">Reply</span>
                    <textarea
                      className="mt-2 w-full min-h-[120px] rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 outline-none resize-y focus:border-teal-500 focus:ring-4 focus:ring-teal-500/5"
                      value={reply}
                      onChange={(event) => setReply(event.target.value)}
                      placeholder="Add more detail, answer a follow-up, or update the support team."
                    />
                  </label>
                  <div className="flex justify-end mt-4">
                    <button
                      type="button"
                      onClick={sendReply}
                      disabled={submitting || !reply.trim()}
                      className="rounded-2xl bg-slate-900 text-white px-5 py-3 text-sm font-semibold disabled:opacity-60 inline-flex items-center gap-2"
                    >
                      <Send size={15} />
                      Send Reply
                    </button>
                  </div>
                </div>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default SupportDesk;
